import { CheckRounded } from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import cx from 'classnames';
import {
  baseSelectStyles,
  getCurrentSelectedOption,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import UnsortedSelect from 'js/components/elements/rselect/UnsortedSelect';
import { Button } from 'new/elements/Button';
import { Switch } from 'new/elements/Switch';
import { TextField } from 'new/elements/TextField';
import {
  DatabaseAccordion,
  DatabaseDetails,
  DatabaseTitle,
} from 'new/elements/flows/Databases/components/DatabaseAccordion/DatabaseAccordion';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import React, { memo, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as TableIcon } from '../../assets/table.svg';
import classes from './Table.module.scss';

const syncModeOptions = [
  {
    value: 'incremental',
    label: 'Auto Increment Strategy',
    description:
      'Automatically detects and syncs only updated rows in each sync cycle based on timestamp or auto-incrementing columns.',
  },
  {
    value: 'full_scan',
    label: 'Full Ingest Strategy',
    description: 'Fetches all data in each sync cycle',
  },
];

interface TableProps {
  database: {
    id: string;
    name: string;
  };
  modified: boolean;
  canPreview?: boolean;
  selected?: boolean;
  expanded?: boolean;
  isExpanded: boolean;
  checked: boolean;
  synced: boolean;
  isDestination: boolean;
  handleExpand: (table: any) => (e, checked) => void;
  onChangeSelectTable: (table: any, isSwitch?: boolean) => (e, checked) => void;
  onClickShowSample: (table: any, isSwitch?: boolean) => (e) => void;
  onShowTableSchema: (table: any, isSwitch?: boolean) => (e: any, checked?: boolean) => void;
  needsSelection?: boolean;
  table: {
    name: string;
    id: number;
    timestampColumn?: string;
    autoIncrementColumn?: string;
    children: any[];
  };
  onChangeResyncTable?: (table: any) => (e: any, checked?: boolean) => void;
  resyncChecked?: boolean;
  isCDCMode?: boolean;
  onChangeTableFilter?: (table: any, filter: string) => void;
  tableFilter?: string;
  columnSelections?: {
    timestampColumn?: string;
    autoIncrementColumn?: string;
  };
  onColumnSelectionChange?: (
    tableName: string,
    columnName: string,
    columnType: 'timestamp' | 'autoIncrement',
    isSelected: boolean,
  ) => void;
  syncMode?: string;
  onSyncModeChange?: (tableName: string, syncMode: string) => void;
}

export const Table = memo<TableProps>((props: TableProps) => {
  const {
    table,
    needsSelection,
    isExpanded,
    isDestination,
    checked,
    synced,
    modified,
    handleExpand,
    onChangeSelectTable,
    onClickShowSample,
    onShowTableSchema,
    onChangeResyncTable,
    resyncChecked,
    isCDCMode,
    onChangeTableFilter,
    tableFilter,
    columnSelections,
    onColumnSelectionChange,
    syncMode,
    onSyncModeChange,
  } = props;
  const loadingProbe = useSelector((state: any) => state.probe.loading?.[table?.id]);
  const [filter, setFilter] = useState(tableFilter || '');
  const [showFilterInput, setShowFilterInput] = useState(!!filter);
  const timestampColumn =
    columnSelections?.timestampColumn === null
      ? null
      : columnSelections?.timestampColumn || table.timestampColumn;
  const autoIncrementColumn =
    columnSelections?.autoIncrementColumn === null
      ? null
      : columnSelections?.autoIncrementColumn || table.autoIncrementColumn;
  const isCDCv2FiltersEnabled = useFeatureFlag('UI_ENABLE_CDC_V2_FILTERS');
  const isFullTableScan = syncMode === 'full_scan';
  const incrementingColumnInfoExists = useMemo(
    () => table?.children?.some((column) => 'autoIncrement' in column || 'timestamp' in column),
    [table?.children],
  );

  const previewButton = (
    <Button
      data-testid={'show-records-button'}
      onClick={onClickShowSample(table)}
      variant="outlined"
      color="primary"
      size="small"
    >
      Preview records
    </Button>
  );

  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    setFilter(newFilter);
    if (onChangeTableFilter) {
      onChangeTableFilter(table, newFilter);
    }
  };

  const toggleFilterInput = (e) => {
    e.stopPropagation();
    setShowFilterInput(!showFilterInput);
  };

  const handleSyncModeChange = (selectedOption) => {
    if (onSyncModeChange) {
      onSyncModeChange(table.name, selectedOption.value);
    }
  };

  return (
    <DatabaseAccordion
      expanded={isExpanded}
      data-testid={`table-node-${table.id}`}
      onChange={handleExpand(table)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <DatabaseTitle
        loading={loadingProbe}
        isStream={isDestination}
        className={cx(classes.tableTitle, {
          [classes.tableModified]: modified,
        })}
      >
        <div className={classes.databaseNameContainer}>
          {needsSelection && isDestination ? (
            <Switch
              data-testid={`check-table-${table.name}`}
              onClick={(e) => e.stopPropagation()}
              onChange={onChangeSelectTable(table, true)}
              checked={synced}
              color="primary"
              size="small"
              className={classes.checkboxCompact}
            />
          ) : null}
          {isDestination ? (
            <Button
              data-testid={`edit-destination-schema-${table.name}`}
              onClick={onShowTableSchema(table)}
              variant="outlined"
              color="primary"
              size="small"
            >
              Destination schema
            </Button>
          ) : null}
          {needsSelection && !isDestination ? (
            <Checkbox
              data-testid={`check-table-${table.name}`}
              onChange={onChangeSelectTable(table, false)}
              checked={checked}
              onClick={(e) => e.stopPropagation()}
              color="primary"
              size="small"
              className={classes.checkboxCompact}
            />
          ) : null}
          <TableIcon />
          <Typography variant="body1">{table.name}</Typography>
        </div>
        <div className={classes.tableActionsContainer} onClick={(e) => e.stopPropagation()}>
          {!isDestination && !isCDCMode ? (
            <>
              {onSyncModeChange && (
                <div className={classes.tableActionItem}>
                  <UnsortedSelect
                    options={syncModeOptions}
                    components={{ Input: SelectInput, Option: SelectOption }}
                    isClearable={false}
                    isSearchable
                    backspaceRemovesValue={false}
                    value={getCurrentSelectedOption(syncModeOptions, syncMode || 'incremental')}
                    onChange={handleSyncModeChange}
                    styles={{
                      ...baseSelectStyles,
                      container: (styles) => ({ ...styles, width: 180 }),
                    }}
                  />
                </div>
              )}
              {onChangeTableFilter && isCDCv2FiltersEnabled && (
                <div className={classes.tableActionItem}>
                  {showFilterInput && (
                    <TextField
                      placeholder="WHERE clause (e.g., id > 100)"
                      style={{ margin: '0 8px', minWidth: '300px' }}
                      value={filter}
                      onChange={handleFilterChange}
                    />
                  )}
                  <Button
                    onClick={toggleFilterInput}
                    variant="outlined"
                    color="primary"
                    size="small"
                  >
                    Filter data
                  </Button>
                </div>
              )}
              {props.canPreview && previewButton}
              {onChangeResyncTable && (
                <div className={classes.tableActionItem}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={onChangeResyncTable(table)}
                        checked={resyncChecked || false}
                        color="primary"
                        size="small"
                        disabled={isFullTableScan}
                      />
                    }
                    label="Re-sync"
                    labelPlacement="end"
                  />
                </div>
              )}
            </>
          ) : props.canPreview ? (
            previewButton
          ) : null}
        </div>
      </DatabaseTitle>
      {isDestination ? null : (
        <DatabaseDetails>
          <div className={classes.detailsTableContainer}>
            <MuiTable size="small" className={classes.table}>
              <TableHead className={classes.head}>
                <TableRow>
                  <TableCell className={classes.headCell}>Column name</TableCell>
                  <TableCell className={classes.headCell}>Type</TableCell>
                  <TableCell className={classes.headCell}>Primary Key</TableCell>
                  {incrementingColumnInfoExists && !isCDCMode && !isFullTableScan && (
                    <>
                      <TableCell className={classes.headCell}>Timestamp</TableCell>
                      <TableCell className={classes.headCell}>Auto-increment</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {table?.children?.map((row) => (
                  <TableRow key={row.name} className={classes.bodyRow}>
                    <TableCell className={classes.bodyCell} component="th" scope="row">
                      {row.name}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>{row.type}</TableCell>
                    <TableCell className={classes.bodyCell}>
                      {row.primaryKey ? <CheckRounded /> : null}
                    </TableCell>
                    {incrementingColumnInfoExists && !isCDCMode && !isFullTableScan && (
                      <>
                        <TableCell className={`${classes.bodyCell} ${classes.columnSelectionCell}`}>
                          {row.timestamp && (
                            <Checkbox
                              checked={timestampColumn === row.name}
                              onChange={(e: any, checked: boolean) =>
                                onColumnSelectionChange?.(
                                  table.name,
                                  row.name,
                                  'timestamp',
                                  checked,
                                )
                              }
                              color="primary"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell className={`${classes.bodyCell} ${classes.columnSelectionCell}`}>
                          {row.autoIncrement && (
                            <Checkbox
                              checked={autoIncrementColumn === row.name}
                              onChange={(e: any, checked: boolean) =>
                                onColumnSelectionChange?.(
                                  table.name,
                                  row.name,
                                  'autoIncrement',
                                  checked,
                                )
                              }
                              color="primary"
                              size="small"
                            />
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </div>
        </DatabaseDetails>
      )}
    </DatabaseAccordion>
  );
});

Table.displayName = 'Table';
